<template>
  <div class="country-form">
    <toolbar
      backRoute="/dashboard/countries"
      :beforeBack="checkForChanges"
      :title="toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="country-item-delete"
      saveButtonLabel="Save country"
      saveButtonEvent="country-item-save"
      :deleteInProgress="removeInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer ref="country-form" tag="form" v-if="!loadError && allDataLoaded">
          <d-row>
            <d-col cols="12">
              <div class="form-group">
                <label for="name">Name:</label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.name"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="6">
              <fieldset>
                <div class="form-group mt-3">
                  <label for="application">Apply to:</label>
                  <d-input v-model="data.application" />
                </div>

                <div class="form-group">
                  <d-checkbox
                    inline
                    v-model="data.agent_mandatory"
                    toggle
                    toggle-small>
                    Agent mandatory
                  </d-checkbox>
                </div>

                <fieldset
                  v-if="preferredHandler"
                  class="small-legend">
                  <legend>Preferred handler</legend>

                  <d-row
                    v-if="preferredHandler"
                    class="pl-4 pr-4">
                    <d-row class="w-100 py-1">
                      <strong class="font-weight-bold mr-2">Name:</strong>
                      {{ preferredHandler.name }}
                    </d-row>
                    <d-row class="w-100 py-1">
                      <strong class="font-weight-bold mr-2">E-mail:</strong>
                      {{ preferredHandler.email }}
                    </d-row>
                    <d-row class="w-100 py-1">
                      <strong class="font-weight-bold mr-2">Phone:</strong>
                      {{ preferredHandler.phone }}
                    </d-row>
                  </d-row>
                </fieldset>

                <div class="form-group">
                  <label for="email">E-mail:</label>
                  <d-textarea
                    v-model="data.email"
                    :value.prop="data.email"
                    :rows="4" />
                </div>

                <div class="form-group">
                  <label for="aftn">AFTN:</label>
                  <d-input v-model="data.aftn" />
                </div>

                <div class="form-group">
                  <label for="phone">Phone:</label>
                  <d-input v-model="data.phone" />
                </div>

                <div class="form-group">
                  <label for="fax">FAX:</label>
                  <d-input v-model="data.fax" />
                </div>

                <div class="form-group">
                  <label for="validity">Valid:</label>
                  <d-input v-model="data.validity" />
                </div>

                <div class="form-group">
                  <label for="changes">Changes:</label>
                  <d-textarea
                    v-model="data.changes"
                    :value.prop="data.changes"
                    rows="6" />
                </div>

                <div class="form-group">
                  <label for="billing">Payment:</label>
                  <d-textarea
                    v-model="data.billing"
                    :value.prop="data.billing"
                    rows="5" />
                </div>
              </fieldset>
            </d-col>

            <d-col cols="6">
              <fieldset class="mb-4">
                <div class="form-group mt-3">
                  <label for="iso_a2">ISO A2:</label>
                  <d-input v-model="data.iso_a2" />
                </div>

                <div class="form-group">
                  <label for="iso_a3">ISO A3:</label>

                  <validation-provider
                    rules="required"
                    name="iso_a3"
                    v-slot="{ errors }">
                    <d-input
                      v-model="data.iso_a3"
                      :state="errors.length ? 'invalid' : null" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <label for="un_region">UN Region:</label>
                  <d-input v-model="data.un_region" />
                </div>

                <div class="form-group">
                  <label for="icao_code1">ICAO CODE1:</label>
                  <d-input v-model="data.icao_code1" />
                </div>

                <div class="form-group">
                  <label for="icao_code2">ICAO CODE2:</label>
                  <d-input v-model="data.icao_code2" />
                </div>

                <div class="form-group">
                  <label for="icao_code2">Field 18:</label>
                  <d-input v-model="data.field_18" />
                </div>
              </fieldset>

              <fieldset class="mt-2">
                <div class="form-group mt-3">
                  <label for="url_more_country">Country info:</label>

                  <d-input-group>
                    <d-input
                      v-model="data.url_more_country" />
                    <d-input-group-addon append>
                      <d-btn
                        theme="primary"
                        :disabled="!data.url_more_country"
                        @click.prevent="openUrl(data.url_more_country)">
                        <i class="material-icons">launch</i>
                      </d-btn>
                    </d-input-group-addon>
                  </d-input-group>
                </div>

                <div class="form-group">
                  <label for="url_vaccination_inf">Entry requirements:</label>

                  <d-input-group>
                    <d-input
                      v-model="data.url_vaccination_inf" />
                    <d-input-group-addon append>
                      <d-btn
                        theme="primary"
                        :disabled="!data.url_vaccination_inf"
                        @click.prevent="openUrl(data.url_vaccination_inf)">
                        <i class="material-icons">launch</i>
                      </d-btn>
                    </d-input-group-addon>
                  </d-input-group>
                </div>

                <div class="form-group">
                  <label for="crew_entry">Crew entry:</label>
                  <d-textarea
                    v-model="data.crew_entry"
                    :value.prop="data.crew_entry"
                    style="height: 100px;"
                    :rows="9" />
                </div>

                <div class="form-group">
                  <label for="lead_res_time">Lead/response time:</label>
                  <d-input v-model="data.lead_res_time" />
                </div>
              </fieldset>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="12">
              <div class="form-group mt-4">
                <label for="notes">Notes:</label>
                <d-textarea
                  v-model="data.notes"
                  :value.prop="data.notes"
                  style="height: 150px;"
                  :rows="9" />
              </div>
            </d-col>
          </d-row>
        </validation-observer>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>

      <handlers-list
        v-if="loadedData"
        title="Agents"
        ref="handlersList"
        referenceType="COUNTRY"
        :referenceId="data.id"
        referenceIdKey="country_id"
        itemName="country"
        popupItemType="agent" />

      <warnings-list
        v-if="loadedData"
        referenceType="COUNTRY"
        :referenceId="data.id"
        itemName="country" />

      <checklist-list
        v-if="loadedData"
        referenceType="COUNTRY"
        :referenceId="data.id"
        itemName="country" />

      <document-downloads-list
        v-if="loadedData"
        :referenceId="data.id"
        itemName="country" />

      <!-- <airports-country-list
        v-if="loadedData"
        referenceType="COUNTRY"
        :referenceId="data.id"
        itemName="country"
        :airports="data.airports" /> -->
    </d-container>
  </div>
</template>

<script>
// import AirportsCountryList from './../form-parts/AirportsCountryList.vue';
import ChecklistList from './../form-parts/CheckLists.vue';
import DocumentDownloadsList from './../form-parts/DocumentDownloads.vue';
import FormUtils from './../../utils/FormUtils.js';
import HandlersList from './../form-parts/Handlers.vue';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import HasUrlField from './../../mixins/HasUrlField.vue';
import ListUtils from './../../utils/ListUtils.js';
import WarningsList from './../form-parts/Warnings.vue';

export default {
  name: 'country-form',
  mixins: [
    HasChangesDetection,
    HasUrlField
  ],
  components: {
    // 'airports-country-list': AirportsCountryList,
    'checklist-list': ChecklistList,
    'document-downloads-list': DocumentDownloadsList,
    'handlers-list': HandlersList,
    'warnings-list': WarningsList
  },
  computed: {
    allDataLoaded () {
      return this.loadedData;
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        return 'Add new country';
      }

      return 'Edit country';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new') {
        return false;
      }

      return 'Delete country';
    }
  },
  data () {
    return {
      data: {
        id: 0,
        name: '',
        aftn: '',
        validity: '',
        email: '',
        phone: '',
        fax: '',
        billing: '',
        iso_a2: '',
        iso_a3: '',
        un_region: '',
        icao_code1: '',
        icao_code2: '',
        field_18: '',
        agent_mandatory: false,
        url_vaccination_inf: '',
        url_more_country: '',
        lead_res_time: '',
        changes: '',
        application: '',
        crew_entry: '',
        notes: '',
        airports: []
      },
      loadError: false,
      loadedData: false,
      removeInProgress: false,
      saveInProgress: false,
      preferredHandler: null
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
    } else {
      this.loadData();
    }

    this.$bus.$on('country-item-delete', this.removeCountry);
    this.$bus.$on('country-item-save', this.validateForm);
    this.$bus.$on('handlers-list-loaded', this.loadPreferredHandler);
  },
  methods: {
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/countries/item/',
        instanceFields: ['id', 'name', 'email', 'phone', 'fax', 'aftn', 'validity', 'agent_mandatory', 'billing', 'url_vaccination_inf', 'url_more_country', 'lead_res_time', 'iso_a2', 'iso_a3', 'un_region', 'icao_code1', 'icao_code2', 'field_18', 'changes', 'application', 'crew_entry', 'notes', 'airports'],
        booleanFields: ['agent_mandatory'],
        loadedKey: 'loadedData'
      });
    },
    removeCountry () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/countries/delete/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'Country removed',
        successText: 'The selected country has been removed',
        confirmTitle: 'Do you really want to remove this country?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/dashboard/countries');
          this.$bus.$emit('countries-list-reload');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['country-form'], this.submitForm);
    },
    submitForm () {
      let endpointUrl = '/countries/store';
      let mode = 'add';

      if (this.id > 0) {
        endpointUrl = '/countries/update/' + parseInt(this.id, 10);
        mode = 'edit';
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: this.data,
        successTitle: mode === 'add' ? 'Country added' : 'Country saved',
        successText: 'The country has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: () => {
          this.$router.push('/dashboard/countries');
          this.$bus.$emit('countries-list-reload');
        }
      });
    },
    loadPreferredHandler () {
      if (!this.$refs['handlersList']) {
        this.preferredHandler = null;
      }

      this.preferredHandler = this.$refs['handlersList'].getPreferredHandler();
    }
  },
  beforeDestroy () {
    this.$bus.$off('country-item-delete', this.removeCountry);
    this.$bus.$off('country-item-save', this.validateForm);
    this.$bus.$off('handlers-list-loaded', this.loadPreferredHandler);
  }
}
</script>
